import { useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { fromCanonicalKg } from '@heraion/shared';

import { Button } from '../primitives/Button';
import type { useTheme } from './theme';
import {
  CALC_EMPTY,
  calcCanonicalKg,
  calcClear,
  calcFromNumber,
  calcPressDecimal,
  calcPressDigit,
  calcPressOp,
  calcSwapUnit,
  calcTape,
  calcValue,
  type CalcOp,
  type CalcState,
} from './calculatorEngine';
import { formatUnitValue, weightLocale } from './plateCalcMath';
import { unitWord } from '../weightUnitLabels';
import type { makeStyles } from './plateCalcModalStyles';
import type { WeightUnit } from './state/loggerDraft';

// The plate calculator's CALCULATOR (LG-37b fetta C) — Antenore's re-spec of
// 10/09, which replaced the morning's design rather than sitting next to it:
// «una calcolatrice semplice … poi il risultato viene messo nel peso totale …
// o chiudo, mi serve solo per vedere o "copio" il risultato».
//
// ⭐ TWO EXITS, and the frequent one writes NOTHING. He described the common
// case himself — you loaded a machine with an armful of plates, you want to
// know what it weighs, you look and you leave. So «Chiudi» is the normal exit
// and «Copia nel peso» is the other one; ⛔ the result never applies by itself.
//
// ⛔ No `=` key, and that is his call (10/09) confirming the approved mockup,
// which draws exactly sixteen keys and a display already showing «60» under a
// tape reading «20 + 20 + 10 + 10». The display answers as you type; see
// calculatorEngine.ts for why that also fixes the evaluation order.

type Props = {
  theme: ReturnType<typeof useTheme>;
  styles: ReturnType<typeof makeStyles>;
  /**
   * The modal's working value in kg RIGHT NOW, or NaN when the field is empty.
   * The calculator opens on it instead of on zero.
   *
   * ⛔ Deliberately NOT called `initialKg`: the modal has a prop by that name
   * and it means something else — the value the modal was OPENED with, frozen
   * from the sets. This one is live, and by the time the calculator expands
   * the athlete may have typed or composed. Same type, two meanings, one
   * keystroke apart is how the wrong one gets wired.
   */
  seedKg: number;
  /**
   * The unit the MODAL is working in — the row's, not the profile's. The chip
   * row below opens on it, so a lifter who switched the set to lb finds the
   * calculator already in pounds instead of having to say so twice.
   *
   * ⛔ Distinct from the `unit` state below, which is the chip the athlete can
   * move: the point of that chip is to do arithmetic in the OTHER unit and
   * still land the answer in this one.
   */
  workingUnit: WeightUnit;
  /** Writes the result into the modal's working value, **in `workingUnit`**. */
  onCopy: (value: number) => void;
  /** Collapses the expansion. The exit that changes nothing. */
  onClose: () => void;
};

type Key =
  | { kind: 'digit'; char: string }
  | { kind: 'decimal' }
  | { kind: 'clear' }
  | { kind: 'op'; op: CalcOp };

const ROWS: Key[][] = [
  [
    { kind: 'digit', char: '7' },
    { kind: 'digit', char: '8' },
    { kind: 'digit', char: '9' },
    { kind: 'op', op: '÷' },
  ],
  [
    { kind: 'digit', char: '4' },
    { kind: 'digit', char: '5' },
    { kind: 'digit', char: '6' },
    { kind: 'op', op: '×' },
  ],
  [
    { kind: 'digit', char: '1' },
    { kind: 'digit', char: '2' },
    { kind: 'digit', char: '3' },
    { kind: 'op', op: '-' },
  ],
  [
    { kind: 'digit', char: '0' },
    { kind: 'decimal' },
    { kind: 'clear' },
    { kind: 'op', op: '+' },
  ],
];

// ⛔ The key SHOWS a minus sign (U+2212), the engine STORES a hyphen. They are
// not the same character: a hyphen is short and sits low, so next to ÷ × + it
// reads as a dash rather than an operator. Keeping the ASCII one inside means
// the tape, the tests and every comparison stay typeable.
const OP_GLYPHS: Record<CalcOp, string> = {
  '+': '+',
  '-': '\u2212',
  '×': '×',
  '÷': '÷',
};

const OP_A11Y_KEYS: Record<CalcOp, string> = {
  '+': 'mobile.plates.calcOpPlus',
  '-': 'mobile.plates.calcOpMinus',
  '×': 'mobile.plates.calcOpTimes',
  '÷': 'mobile.plates.calcOpDivide',
};

export function PlateCalcCalculatorSection({
  theme,
  styles,
  seedKg,
  workingUnit,
  onCopy,
  onClose,
}: Props) {
  const { t, i18n } = useTranslation();
  // ⭐ Opens on the weight already in the field (Antenore, 11/09). The common
  // gesture is arithmetic ON that number — «45, plus the two twenties I just
  // put on» — and starting at zero made the athlete retype what the screen was
  // already showing.
  //
  // ⚠️ Lazy initialiser, and it is load-bearing: this section UNMOUNTS when
  // the disclosure collapses, so the initialiser runs on every open and the
  // seed is always the CURRENT value, not the one from the first time. A plain
  // `useState(seed)` with an effect to re-sync would be the same thing with a
  // frame of the wrong number in it.
  const [state, setState] = useState<CalcState>(() =>
    Number.isFinite(seedKg) && seedKg > 0
      ? // The seed arrives as canonical kilos, so it is also the placed value:
        // a swap to pounds and back has to land on the number that was in the
        // field, not on what reading the pounds back would give (`LG-85`).
        // ⚠️ What is DISPLAYED is that seed in the working unit — the chip below
        // opens there, and a display that disagreed with its own unit chip
        // would be LG-81 inside the calculator.
        calcFromNumber(fromCanonicalKg(seedKg, workingUnit), seedKg)
      : CALC_EMPTY,
  );
  const [unit, setUnit] = useState<'kg' | 'lb'>(workingUnit);
  // ⚠️ The display below is a live region, and since the seed it mounts with a
  // number ALREADY in it. A live region that arrives pre-filled announces at
  // mount, right on top of the disclosure button announcing that it expanded:
  // two utterances for one gesture, and on TalkBack they talk over each other.
  // ⇒ It starts silent and becomes live at the first press, which is the only
  // moment there is something the athlete did not already hear.
  // ⛔ A ref, not state: the value is only read while rendering the attribute,
  // and every press re-renders anyway. A second `useState` here would be a
  // render that changes nothing.
  const interacted = useRef(false);

  // The decimal mark this athlete's keyboard would write. Taken from the
  // locale rather than hard-coded: the engine stores `.`, the key shows «,»
  // in it-IT / fr-FR / de-DE, and the tape has to agree with the key that
  // wrote it.
  //
  // ⚠️ Through `weightLocale`, which is the same fallback the weight formatters
  // use — and the same reason: `i18n` itself is absent whenever `react-i18next`
  // is mocked with only a `t`, and `i18n.resolvedLanguage` on `undefined`
  // throws before the panel renders a single key.
  const locale = weightLocale(i18n);
  const decimal = useMemo(
    () => (1.1).toLocaleString(locale).replace(/\d/g, ''),
    [locale],
  );

  const value = calcValue(state);
  const tape = calcTape(state, decimal);
  // A calculator at rest reads 0, not a dash: the dash would be a state to
  // decode, and there is nothing wrong yet. `value` is still null, so
  // "copy into the weight" stays disabled — the zero is what it LOOKS like,
  // not a number anyone can carry out.
  const display =
    value === null
      ? '0'
      : formatUnitValue(value, unit, locale);

  const press = (key: Key) => {
    interacted.current = true;
    setState((s) => {
      switch (key.kind) {
        case 'digit':
          return calcPressDigit(s, key.char);
        case 'decimal':
          return calcPressDecimal(s);
        case 'clear':
          return calcClear();
        case 'op':
          return calcPressOp(s, key.op);
      }
    });
  };

  // ⭐ LG-93 — la conversione del display è RIMASTA, è cambiato chi la innesca.
  // Prima la chiedevano i due chip qui dentro; ora l'unità si ruota una volta
  // sola nella riga TARGET, e la calcolatrice la SEGUE.
  //
  // ⛔ Non si è potuta cancellare insieme ai chip, ed è il punto: il numero a
  // display e l'espressione in corso sono in un'unità, e se `workingUnit`
  // cambia sotto di loro senza convertirli, «132» resta 132 dell'altra unità —
  // che è la forma esatta di `LG-81`. `calcSwapUnit` porta con sé i chili
  // canonici, quindi kg → lb → kg torna al numero di partenza.
  //
  // ⚠️ `interacted` NON si alza qui: ruotare l'unità non è l'atleta che digita,
  // e marcarlo come interazione farebbe credere alla sezione di avere un
  // risultato suo da difendere al prossimo re-seed.
  //
  // ⛔ Lettura e mutazione SEPARATE, e non è stile: la prima stesura chiamava
  // `setState` dentro l'updater di `setUnit`. Un updater deve essere PURO —
  // React lo invoca due volte in StrictMode apposta per scovare chi non lo è —
  // quindi `calcSwapUnit` sarebbe girata due volte e la calcolatrice si
  // sarebbe convertita due volte, in silenzio. Lo stesso difetto che
  // `pendingStart.tsx` ha già preso una volta in questo repo, preso di nuovo.
  useEffect(() => {
    if (unit === workingUnit) return;
    setState((s) => calcSwapUnit(s, unit, workingUnit));
    setUnit(workingUnit);
  }, [unit, workingUnit]);

  // ⭐ Since fetta E1 the weight field speaks the ROW's unit, so the common
  // case is no conversion at all: the chip and the field agree, and the number
  // the athlete is LOOKING AT is the number that lands. Only a deliberate
  // mismatch — doing the sum in pounds for a metric row — spends a trip
  // through canonical kilos.
  //
  // ⛔ What must never happen is the shape of LG-81: a display reading 132
  // landing as 132 of the other unit. The branch below is what prevents it,
  // and it is one branch, here, at the exit — not a rule each caller repeats.
  const copyValue = useMemo(() => {
    if (unit === workingUnit) return calcValue(state);
    const kg = calcCanonicalKg(state, unit);
    return kg === null ? null : fromCanonicalKg(kg, workingUnit);
  }, [state, unit, workingUnit]);
  const canCopy = copyValue !== null && copyValue > 0;

  return (
    <View style={styles.calcSection}>
      {/* ⛔ LG-93 — la riga dei due chip kg/lb E' USCITA DA QUI, sua decisione
          dell'11/09: «i due chip spariscono dalla calcolatrice e si guadagna
          una linea». L'unità si sceglie una volta sola, nello switcher della
          riga TARGET, e la calcolatrice la eredita — era l'unico posto che
          questo stesso documento dichiarava da evitare, e ci era finita. */}
      <Text style={styles.calcTape} numberOfLines={1}>
        {tape}
      </Text>
      <Text
        style={styles.calcDisplay}
        numberOfLines={1}
        // The one thing on this panel a screen reader must read as a value,
        // not as decoration — the tape above is the working, this is the answer.
        accessibilityRole="text"
        // ⭐ It has to speak BY ITSELF. With no `=` key there is no moment
        // that says "here is your answer": the display IS the response to
        // every keypress, so a reader who had to navigate back to it after
        // each digit would get no feedback from the keypad at all.
        // ⛔ But not at mount — see `interacted`.
        accessibilityLiveRegion={interacted.current ? 'polite' : 'none'}
        accessibilityLabel={t('mobile.plates.calcResult', {
          value: display,
          unit: unitWord(t, unit),
        })}
      >
        {display}
      </Text>

      {ROWS.map((row, rowIdx) => (
        <View key={rowIdx} style={styles.keypadRow}>
          {row.map((key) => {
            const isOp = key.kind === 'op';
            const label =
              key.kind === 'digit'
                ? key.char
                : key.kind === 'decimal'
                  ? decimal
                  : key.kind === 'clear'
                    ? t('mobile.plates.calcClearKey')
                    : OP_GLYPHS[key.op];
            const spoken =
              key.kind === 'op'
                ? t(OP_A11Y_KEYS[key.op])
                : key.kind === 'clear'
                  ? t('mobile.plates.calcClear')
                  : key.kind === 'decimal'
                    ? t('mobile.plates.calcDecimal')
                    : label;
            return (
              <Pressable
                key={label + (isOp ? 'op' : '')}
                accessibilityRole="button"
                accessibilityLabel={spoken}
                onPress={() => press(key)}
                // ⛔ No hitSlop: the box IS 44. See the note on `calcKey`.
                style={({ pressed }) => [
                  styles.calcKey,
                  isOp && styles.calcKeyOp,
                  pressed && styles.calcKeyPressed,
                ]}
              >
                <Text
                  style={[styles.calcKeyText, isOp && styles.calcKeyOpText]}
                >
                  {label}
                </Text>
              </Pressable>
            );
          })}
        </View>
      ))}

      <View style={styles.calcExits}>
        <Button
          theme={theme}
          variant="ghost"
          label={t('mobile.plates.calcDismiss')}
          onPress={onClose}
          style={styles.calcExit}
          // Two buttons side by side: horizontal slop would overlap the
          // neighbour, so the expansion is vertical only.
          hitSlop={{ top: 8, bottom: 8 }}
        />
        <Button
          theme={theme}
          variant="primary"
          label={t('mobile.plates.calcCopy')}
          disabled={!canCopy}
          onPress={() => {
            if (copyValue === null) return;
            onCopy(copyValue);
          }}
          style={styles.calcExitPrimary}
          hitSlop={{ top: 8, bottom: 8 }}
          accessibilityHint={
            canCopy ? undefined : t('mobile.plates.calcCopyBlocked')
          }
        />
      </View>
    </View>
  );
}
